import { favoritesApi, favoriteStonesApi, creditApi, checkoutApi, type FinancingTermsResponse, type PaymentOptions } from '../api/endpoints'

/**
 * Session-scoped caches for data the stone modal reads on open but that does
 * NOT change between opens (or between prev/next hops within a session):
 * the buyer's favorite-supplier set and their Aska Credit financing standing.
 *
 * Before this, MediaModal's SupplierContact re-fetched favoritesApi.list() and
 * BuyViaDiaex re-fetched creditApi.financing() on EVERY mount — i.e. every
 * modal open AND every prev/next arrow. Two network round-trips per stone the
 * buyer glances at. Both answers are the same all session, so we fetch each
 * once, hand out the cached value synchronously thereafter, and (for
 * favorites) keep it live on toggle. Shared by the web modal and the mobile
 * StoneDetailScreen so both stop re-fetching.
 *
 * Everything here is best-effort: a failed fetch resolves to an empty/absent
 * value so the UI degrades exactly as the per-mount fetch used to (unhearted /
 * card-only), never throwing into a caller.
 */

// ─── Favorite suppliers ───────────────────────────────────────────────────
// The cache is the SET of supplierIds the buyer has hearted. Components read
// it to paint the heart and subscribe so a toggle anywhere updates every
// mounted heart at once.

let favSet: Set<number> | null = null
let favInflight: Promise<Set<number>> | null = null
const favSubs = new Set<() => void>()

function notifyFav() {
  for (const fn of favSubs) fn()
}

/**
 * Fetch the favorite-supplier set once per session. Concurrent callers share
 * the in-flight promise; subsequent callers get the resolved cache. A failure
 * resolves to an empty set (same degrade as the old per-mount catch) without
 * poisoning the cache, so a later call can retry.
 */
export function loadFavoriteSuppliers(): Promise<Set<number>> {
  if (favSet) return Promise.resolve(favSet)
  if (favInflight) return favInflight
  favInflight = favoritesApi
    .list()
    .then((r) => {
      favSet = new Set(r.suppliers.map((s) => s.supplierId))
      favInflight = null
      notifyFav()
      return favSet
    })
    .catch(() => {
      favInflight = null
      // Don't cache the failure — leave favSet null so a later mount retries.
      return new Set<number>()
    })
  return favInflight
}

/** Synchronous read for a single supplier; null when not yet loaded. */
export function isFavoriteSupplier(supplierId: number): boolean | null {
  if (!favSet) return null
  return favSet.has(supplierId)
}

/** Reflect a toggle result into the cache and wake every subscribed heart. */
export function setFavoriteSupplier(supplierId: number, favorited: boolean) {
  if (!favSet) favSet = new Set<number>()
  if (favorited) favSet.add(supplierId)
  else favSet.delete(supplierId)
  notifyFav()
}

/** Subscribe to favorite-set changes (returns an unsubscribe fn). */
export function subscribeFavorites(fn: () => void): () => void {
  favSubs.add(fn)
  return () => { favSubs.delete(fn) }
}

// ─── Favorite stones ───────────────────────────────────────────────────────
// Parallel to favorite suppliers, but keyed by stockId (string). The cache is
// the SET of stockIds the buyer has bookmarked; the bookmark button on every
// stone surface reads it to paint itself and subscribes so a toggle anywhere
// repaints every mounted bookmark. Only the identifier is cached here — the
// stone's live data (price, availability) is re-fetched through the
// marketplace API on the /favorites page, never stored.

let favStoneSet: Set<string> | null = null
let favStoneInflight: Promise<Set<string>> | null = null
const favStoneSubs = new Set<() => void>()

function notifyFavStone() {
  for (const fn of favStoneSubs) fn()
}

/**
 * Fetch the favorite-stone set once per session. Concurrent callers share the
 * in-flight promise; a failure resolves to an empty set without poisoning the
 * cache so a later mount retries.
 */
export function loadFavoriteStones(): Promise<Set<string>> {
  if (favStoneSet) return Promise.resolve(favStoneSet)
  if (favStoneInflight) return favStoneInflight
  favStoneInflight = favoriteStonesApi
    .list()
    .then((r) => {
      favStoneSet = new Set(r.stones.map((s) => s.stockId))
      favStoneInflight = null
      notifyFavStone()
      return favStoneSet
    })
    .catch(() => {
      favStoneInflight = null
      return new Set<string>()
    })
  return favStoneInflight
}

/** Synchronous read for a single stone; null when not yet loaded. */
export function isFavoriteStone(stockId: string): boolean | null {
  if (!favStoneSet) return null
  return favStoneSet.has(stockId)
}

/** Reflect a toggle result into the cache and wake every subscribed bookmark. */
export function setFavoriteStone(stockId: string, favorited: boolean) {
  if (!favStoneSet) favStoneSet = new Set<string>()
  if (favorited) favStoneSet.add(stockId)
  else favStoneSet.delete(stockId)
  notifyFavStone()
}

/** Subscribe to favorite-stone-set changes (returns an unsubscribe fn). */
export function subscribeFavoriteStones(fn: () => void): () => void {
  favStoneSubs.add(fn)
  return () => { favStoneSubs.delete(fn) }
}

// ─── Financing standing ────────────────────────────────────────────────────
// The buyer's Aska Credit terms don't change mid-session; cache the single
// promise so BuyViaDiaex reads it once instead of on every stone.

let financingInflight: Promise<FinancingTermsResponse | null> | null = null
let financingResolved: FinancingTermsResponse | null | undefined

export function loadFinancing(): Promise<FinancingTermsResponse | null> {
  if (financingResolved !== undefined) return Promise.resolve(financingResolved)
  if (financingInflight) return financingInflight
  financingInflight = creditApi
    .financing()
    .then((f) => { financingResolved = f; financingInflight = null; return f })
    .catch(() => {
      // Card-only path still works; don't cache the failure so a later mount
      // can retry, matching the old per-mount best-effort behaviour.
      financingInflight = null
      return null
    })
  return financingInflight
}

// ─── Payment rails ─────────────────────────────────────────────────────────
// Region-decided rails (server: /checkout/payment-options) don't change
// mid-session either — one fetch for every checkout surface. null = fetch
// failed → surfaces render the card-only baseline; the server re-enforces
// every rail at place time regardless.

let railsInflight: Promise<PaymentOptions | null> | null = null
let railsResolved: PaymentOptions | null | undefined

export function loadPaymentOptions(): Promise<PaymentOptions | null> {
  if (railsResolved !== undefined) return Promise.resolve(railsResolved)
  if (railsInflight) return railsInflight
  railsInflight = checkoutApi
    .paymentOptions()
    .then((o) => { railsResolved = o; railsInflight = null; return o })
    .catch(() => {
      // Don't cache the failure — a later mount can retry.
      railsInflight = null
      return null
    })
  return railsInflight
}

/** Test/sign-out hook — drop all cached session data. */
export function resetSessionCache() {
  favSet = null
  favInflight = null
  favSubs.clear()
  favStoneSet = null
  favStoneInflight = null
  favStoneSubs.clear()
  financingInflight = null
  financingResolved = undefined
  railsInflight = null
  railsResolved = undefined
}
